import type { Writable } from "svelte/store";
import { writable } from "svelte/store";
import * as filepath from "./filepath";
import { FS } from "./fs";
import type { BoxConfig } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { sleep, throwIf } from "./util";

// Box adds Access-Control-Allow-Origin only after the token authenticates, and
// only for domains listed on that Box app. A 401 has no CORS header, so the
// browser cannot read it. Every call goes through /api/box. The access token
// is the X-Box-Token header, never a query value.
// https://developer.box.com/guides/security/cors

// Keep in sync with boxSimpleMax in server/box.go. The server uploads anything
// smaller as one multipart POST and starts an upload session above that.
export const boxSimpleMax = 50_000_000;

export const prop_box_sha1 = "sha1";

const listFields = "type,id,name,size,modified_at,sha1,item_status";

export interface BoxItem {
  id?: string;
  type?: string;
  name?: string;
  size?: number;
  modified_at?: string;
  sha1?: string;
  item_status?: string;
}

export class BoxStatusError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(boxErrorMessage(status, body));
    this.name = "BoxStatusError";
    this.status = status;
    this.body = body;
  }
}

export function boxUsesSimpleUpload(size: number): boolean {
  return size < boxSimpleMax;
}

export function boxErrorMessage(status: number, body: any): string {
  if (body && typeof body.error_description == "string" && body.error_description) {
    return body.error_description;
  }
  if (body && typeof body.message == "string" && body.message) {
    return body.message;
  }
  if (body && typeof body.error == "string" && body.error) {
    return body.error;
  }
  if (status == 401) {
    return "Box access token expired";
  }
  return `Box ${status}`;
}

export function boxConflictFileID(body: any): string {
  let conflicts = body?.context_info?.conflicts;
  if (Array.isArray(conflicts)) {
    conflicts = conflicts[0];
  }
  if (!conflicts || conflicts.type != "file" || conflicts.id == null || conflicts.id === "") {
    return "";
  }
  return String(conflicts.id);
}

export function boxNewAttributes(name: string, parentId: string, modified: string): string {
  return JSON.stringify({
    name: name,
    parent: { id: parentId },
    content_modified_at: modified,
  });
}

export function boxFilesToWire(items: BoxItem[]): any[][] {
  let wires: any[][] = [];
  for (let item of items) {
    if (!item.id || !item.name || item.name == "." || item.name == "..") {
      continue;
    }
    if (item.name.includes("/") || item.name.includes("\\") || item.name.includes("\0")) {
      continue;
    }
    if (item.item_status && item.item_status != "active") {
      continue;
    }
    if (item.type != "file" && item.type != "folder") {
      continue;
    }
    let isDir = item.type == "folder";
    let size = isDir ? -1 : item.size || 0;
    let lastMod = item.modified_at ? Date.parse(item.modified_at) : 0;
    if (Number.isNaN(lastMod)) {
      lastMod = 0;
    }
    let meta: any[] = [String(item.id), item.name, size, lastMod, isDir ? [] : null];
    if (!isDir && item.sha1) {
      meta.push(prop_box_sha1, item.sha1);
    }
    wires.push(meta);
  }
  return wires;
}

class FileSystemBox extends FS {
  supportsDirRename = true;

  constructor(config: BoxConfig) {
    super(config);
  }

  get boxConfig(): BoxConfig {
    return this.config as BoxConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let parentId = "0";
      if (dirPath != "/") {
        await this.readParentDirs(dirPath, force, progress);
        parentId = this.entryId(dirPath);
      }
      let items = await boxList(this.boxConfig.accessToken, parentId);
      return FsDir.fromWire(dirPath, boxFilesToWire(items));
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let id = this.item(path).id;
    let url = mkURL("/api/box/download", "id", id);
    let xhr = await boxSend("GET", url, this.boxConfig.accessToken, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let name = filepath.base(path);
    if (!name || name == "." || name == "..") {
      throw new Error("path is required");
    }
    let parentId = await this.parentId(path);
    let modified = file instanceof File ? new Date(file.lastModified).toISOString() : new Date().toISOString();
    let url = mkURL("/api/box/upload", "parent", parentId, "name", name, "modified", modified);
    await boxSend("PUT", url, this.boxConfig.accessToken, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let item = this.item(path);
    let name = filepath.base(newPath);
    if (!name || name == "." || name == "..") {
      throw new Error("path is required");
    }
    let parentId = await this.parentId(newPath);
    let kind = item.folder ? "folders" : "files";
    await boxJSON(this.boxConfig.accessToken, "PUT", `/${kind}/${item.id}`, {
      name: name,
      parent: { id: parentId },
    });
  }

  async createDirectory(dirPath: string): Promise<void> {
    let name = filepath.base(dirPath);
    if (!name || name == "." || name == "..") {
      throw new Error("path is required");
    }
    let parentId = await this.parentId(dirPath);
    try {
      await boxJSON(this.boxConfig.accessToken, "POST", "/folders", {
        name: name,
        parent: { id: parentId },
      });
    } catch (e) {
      if (e instanceof BoxStatusError && e.status == 409 && e.body?.code == "item_name_in_use") {
        return;
      }
      throw e;
    }
  }

  async deleteFile(path: string): Promise<void> {
    let item = this.item(path);
    let token = this.boxConfig.accessToken;
    if (item.folder) {
      await boxJSON(token, "DELETE", `/folders/${item.id}?recursive=true`);
      return;
    }
    await boxJSON(token, "DELETE", `/files/${item.id}`);
  }

  item(path: string): { id: string; folder: boolean } {
    let bare = filepath.stripDirSlash(path);
    if (bare == "" || bare == "/") {
      throw new Error("path is required");
    }
    let found = this.findEntries(bare);
    let entry = found.entries[0];
    return {
      id: found.dir.entryId(entry),
      folder: found.dir.entryIsDir(entry) || path.endsWith("/"),
    };
  }

  entryId(path: string): string {
    return this.item(path).id;
  }

  async parentId(path: string): Promise<string> {
    let dir = filepath.dir(filepath.stripDirSlash(path));
    if (dir == "/" || dir == "") {
      return "0";
    }
    if (!this.dirs.get(dir)) {
      await this.readDir(dir, false, writable(""));
    }
    return this.entryId(dir);
  }
}

export function makeFileSystemBox(config: BoxConfig): FileSystemBox {
  return new FileSystemBox(config);
}

export async function boxAccount(token: string): Promise<{ name: string; login: string }> {
  let js = await boxJSON(token, "GET", "/users/me?fields=id,name,login");
  return {
    name: js.name || "",
    login: js.login || "",
  };
}

async function boxList(token: string, folderId: string): Promise<BoxItem[]> {
  let items: BoxItem[] = [];
  let marker = "";
  for (let page = 0; page < 50; page++) {
    let url = `/folders/${folderId}/items?fields=${listFields}&limit=1000&usemarker=true`;
    if (marker) {
      url += `&marker=${encodeURIComponent(marker)}`;
    }
    let js = await boxJSON(token, "GET", url);
    items.push(...((js.entries || []) as BoxItem[]));
    let next = (js.next_marker as string) || "";
    if (!next || next == marker) {
      return items;
    }
    marker = next;
  }
  throw new Error("too many files in this folder");
}

async function boxJSON(token: string, method: string, path: string, body?: unknown): Promise<any> {
  checkToken(token);
  let lastErr = "Box request failed";
  for (let attempt = 0; attempt < 4; attempt++) {
    let rsp: Response;
    try {
      rsp = await fetch("/api/box/json", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Box-Token": token },
        body: JSON.stringify({ method: method, path: path, body: body ?? null }),
      });
    } catch (e: any) {
      lastErr = e?.message || String(e);
      await sleep(400 * (attempt + 1));
      continue;
    }
    if (rsp.status == 204) {
      return {};
    }
    let text = await rsp.text();
    let js: any = {};
    if (text) {
      try {
        js = JSON.parse(text);
      } catch {
        js = {};
      }
    }
    if (rsp.status == 429 || rsp.status >= 500) {
      lastErr = boxErrorMessage(rsp.status, js);
      await sleep(400 * (attempt + 1));
      continue;
    }
    if (!rsp.ok) {
      throw new BoxStatusError(rsp.status, js);
    }
    return js;
  }
  throw new Error(lastErr);
}

function boxSend(
  method: string,
  url: string,
  token: string,
  data: Blob | FormData | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  checkToken(token);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    xhr.setRequestHeader("X-Box-Token", token);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      void boxXHRError(xhr).then(
        (msg) => reject(msg),
        () => reject(new Error(`Box ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("Box request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT" || method == "POST") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    xhr.send(data ?? null);
  });
}

async function boxXHRError(xhr: XMLHttpRequest): Promise<Error> {
  let text = "";
  if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  } else if (xhr.response instanceof Blob) {
    text = await xhr.response.text();
  }
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  return new BoxStatusError(xhr.status, js);
}

function checkToken(token: string) {
  if (!token || token.length > 4096 || /[\u0000-\u001f\u007f]/.test(token)) {
    throw new Error("bad access token");
  }
}
